import React, { useLayoutEffect, useRef } from 'react';

/**
 * Scrolls a tab strip just far enough that the tab is wholly in view. Only the strip moves: neither the
 * frame nor the host page scrolls for it, as they would for `scrollIntoView`.
 */
export function scrollTabIntoStripView(strip: HTMLElement, tab: HTMLElement): void {
  const stripBox = strip.getBoundingClientRect();
  const tabBox = tab.getBoundingClientRect();
  if (tabBox.left < stripBox.left) {
    strip.scrollLeft += tabBox.left - stripBox.left;
  } else if (tabBox.right > stripBox.right) {
    strip.scrollLeft += tabBox.right - stripBox.right;
  }
}

/**
 * A row of tabs that may be wider than the screen. It scrolls sideways in a strip of its own rather than
 * running past the page's edge, which cuts it off, and the tab in view (the one marked the current page
 * or selected) is scrolled into the strip whenever it changes or tabs come and go. The scrollbar is left
 * out: the tab cut off at the strip's edge shows there is more. Its tabs carry
 * `BRAND_PRESENCE_TAB_IN_STRIP_CLASSES`.
 */
export function BrandPresenceTabStrip({
  inView,
  className,
  children,
  ...attributes
}: React.HTMLAttributes<HTMLDivElement> & {
  /** Which tab is in view; a change scrolls it into the strip. */
  inView: string;
}): React.ReactElement {
  const stripRef = useRef<HTMLDivElement>(null);
  const tabCount = React.Children.toArray(children).length;

  useLayoutEffect(() => {
    const strip = stripRef.current;
    const tab = strip?.querySelector<HTMLElement>('[aria-current="page"], [aria-selected="true"]');
    if (strip && tab) {
      scrollTabIntoStripView(strip, tab);
    }
  }, [inView, tabCount]);

  return (
    <div
      ref={stripRef}
      className={['flex min-w-0 items-center gap-1 overflow-x-auto [scrollbar-width:none]', className].filter(Boolean).join(' ')}
      {...attributes}
    >
      {children}
    </div>
  );
}
